import React, { useMemo, useState, createElement } from 'react';
import { Users, Search, Download, Eye, Ban, UserCheck, MessageSquare, AlertTriangle, CreditCard, ShoppingBag, MoreVertical, Video, RefreshCw, Star, Calendar, MapPin, Clock, Mail, Phone, Edit3, Tag, ChevronDown, X, CheckSquare, Square, FileText, Activity, ThumbsUp, Zap } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { Input } from '../ui/Input';
import { Tabs } from '../ui/Tabs';
interface AdminUsersManagerProps {
  users: any[];
  orders: any[];
  astrologers: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  setCreatePanel?: (panel: any) => void;
  success: (msg: string) => void;
}
export function AdminUsersManager({
  users: initialUsers,
  orders,
  astrologers,
  setActiveTab,
  setDetailPanel: setExternalDetailPanel,
  setCreatePanel,
  success
}: AdminUsersManagerProps) {
  // Enhance users with mock data for new fields if they don't exist
  const [users, setUsers] = useState(() => initialUsers.map(u => ({
    ...u,
    lastLogin: u.lastLogin || '2 часа назад',
    country: u.country || ['Россия', 'Беларусь', 'Казахстан', 'Украина'][Math.floor(Math.random() * 4)],
    language: u.language || 'Русский',
    consultations: u.consultations !== undefined ? u.consultations : Math.floor(Math.random() * 5),
    lastOrder: u.lastOrder || u.date,
    balance: u.balance ?? Math.floor(Math.random() * 15000),
    notes: u.notes || '',
    riskFlag: u.riskFlag || null,
    isVip: u.isVip || parseInt(u.spent?.replace(/\D/g, '') || '0') > 30000,
    hasComplaints: u.hasComplaints || Math.random() > 0.8,
    hasActiveChats: u.hasActiveChats || Math.random() > 0.7,
    hasPaymentIssues: u.hasPaymentIssues || Math.random() > 0.9,
    phone: u.phone || '+7 (999) 000-00-00',
    refunds: Math.random() > 0.8 ? 1 : 0
  })));
  // --- STATES ---
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  // Detail Panel State (Internal to handle tabs and rich data)
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  // Action Modals State
  const [noteModal, setNoteModal] = useState<{
    userId: number;
    text: string;
  } | null>(null);
  const [riskModal, setRiskModal] = useState<{
    userId: number;
    flag: string;
  } | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
  // Filter State
  const [filters, setFilters] = useState({
    status: 'all',
    subscription: 'all',
    ordersMin: '',
    ordersMax: '',
    spentMin: '',
    spentMax: '',
    dateFrom: '',
    dateTo: '',
    country: 'all',
    language: 'all',
    hasComplaints: 'all',
    hasActiveOrders: 'all',
    hasConsultations: 'all'
  });
  const itemsPerPage = 15;
  // --- FILTERING LOGIC ---
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      // Search
      const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase()) || u.id.toString().includes(searchQuery) || u.phone && u.phone.includes(searchQuery);
      if (!matchesSearch) return false;
      // Advanced Filters
      if (filters.status !== 'all' && u.status !== filters.status) return false;
      if (filters.subscription !== 'all') {
        const hasSub = !!u.subscription;
        if (filters.subscription === 'true' && !hasSub) return false;
        if (filters.subscription === 'false' && hasSub) return false;
      }
      const ordersCount = parseInt(u.orders) || 0;
      if (filters.ordersMin && ordersCount < parseInt(filters.ordersMin)) return false;
      if (filters.ordersMax && ordersCount > parseInt(filters.ordersMax)) return false;
      const spentAmount = parseInt(u.spent?.replace(/\D/g, '') || '0');
      if (filters.spentMin && spentAmount < parseInt(filters.spentMin)) return false;
      if (filters.spentMax && spentAmount > parseInt(filters.spentMax)) return false;
      if (filters.country !== 'all' && u.country !== filters.country) return false;
      if (filters.language !== 'all' && u.language !== filters.language) return false;
      if (filters.hasComplaints !== 'all') {
        if (filters.hasComplaints === 'true' && !u.hasComplaints) return false;
        if (filters.hasComplaints === 'false' && u.hasComplaints) return false;
      }
      if (filters.hasConsultations !== 'all') {
        const hasCons = (u.consultations || 0) > 0;
        if (filters.hasConsultations === 'true' && !hasCons) return false;
        if (filters.hasConsultations === 'false' && hasCons) return false;
      }
      return true;
    });
  }, [users, searchQuery, filters]);
  // --- PAGINATION ---
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const currentUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // --- KPIs ---
  const kpis = useMemo(() => {
    return {
      total: users.length,
      newToday: 12,
      active: users.filter(u => u.status === 'active').length,
      blocked: users.filter(u => u.status === 'blocked').length,
      withSub: users.filter(u => !!u.subscription).length,
      withComplaints: users.filter(u => u.hasComplaints).length,
      pendingOrders: users.filter(u => u.orders > 0 && Math.random() > 0.7).length,
      refunds: users.filter(u => u.refunds > 0).length,
      activeConsultations: users.filter(u => u.consultations > 0 && Math.random() > 0.8).length,
      vip: users.filter(u => u.isVip).length
    };
  }, [users]);
  // --- HANDLERS ---
  const handleExport = () => {
    const headers = ['ID', 'Имя', 'Email', 'Телефон', 'Дата регистрации', 'Страна', 'Заказы', 'Консультации', 'Потрачено', 'Подписка', 'Статус'];
    const rows = filteredUsers.map(u => [u.id, u.name, u.email, u.phone || '', u.date, u.country, u.orders, u.consultations, u.spent, u.subscription || '—', u.status === 'active' ? 'Активен' : 'Заблокирован']);
    const csvContent = [headers.join(','), ...rows.map(r => r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    success(`Экспорт ${filteredUsers.length} пользователей завершён`);
  };
  const handleToggleBlock = (userId: number) => {
    setUsers(users.map(u => {
      if (u.id === userId) {
        const newStatus = u.status === 'active' ? 'blocked' : 'active';
        success(newStatus === 'active' ? `${u.name} разблокирован` : `${u.name} заблокирован`);
        return {
          ...u,
          status: newStatus
        };
      }
      return u;
    }));
    setActionMenuOpen(null);
  };
  const handleSaveNote = () => {
    if (!noteModal) return;
    setUsers(users.map(u => u.id === noteModal.userId ? {
      ...u,
      notes: noteModal.text
    } : u));
    success('Заметка сохранена');
    setNoteModal(null);
    setActionMenuOpen(null);
  };
  const handleSaveRisk = () => {
    if (!riskModal) return;
    setUsers(users.map(u => u.id === riskModal.userId ? {
      ...u,
      riskFlag: riskModal.flag || null
    } : u));
    success('Risk-флаг обновлен');
    setRiskModal(null);
    setActionMenuOpen(null);
  };
  const toggleSelectAll = () => {
    if (selectedUsers.size === currentUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(currentUsers.map(u => u.id)));
    }
  };
  const toggleSelectUser = (id: number) => {
    const newSet = new Set(selectedUsers);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedUsers(newSet);
  };
  const handleMassAction = (action: string) => {
    if (selectedUsers.size === 0) return;
    switch (action) {
      case 'block':
        setUsers(users.map(u => selectedUsers.has(u.id) ? {
          ...u,
          status: 'blocked'
        } : u));
        success(`${selectedUsers.size} пользователей заблокировано`);
        break;
      case 'email':
        success(`Открыта форма рассылки для ${selectedUsers.size} пользователей`);
        break;
      case 'export':
        {
          const headers = ['ID', 'Имя', 'Email', 'Телефон', 'Статус'];
          const selectedArr = users.filter(u => selectedUsers.has(u.id));
          const rows = selectedArr.map(u => [u.id, u.name, u.email, u.phone || '', u.status === 'active' ? 'Активен' : 'Заблокирован']);
          const csvContent = [headers.join(','), ...rows.map(r => r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
          const blob = new Blob(['\uFEFF' + csvContent], {
            type: 'text/csv;charset=utf-8;'
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `users_selected_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          success(`Экспорт ${selectedUsers.size} выбранных пользователей завершён`);
        }
        break;
      case 'tag':
        success(`Тег добавлен ${selectedUsers.size} пользователям`);
        break;
    }
    setSelectedUsers(new Set());
  };
  const applyKpiFilter = (filterType: string) => {
    // Reset filters first
    const newFilters = {
      status: 'all',
      subscription: 'all',
      ordersMin: '',
      ordersMax: '',
      spentMin: '',
      spentMax: '',
      dateFrom: '',
      dateTo: '',
      country: 'all',
      language: 'all',
      hasComplaints: 'all',
      hasActiveOrders: 'all',
      hasConsultations: 'all'
    };
    switch (filterType) {
      case 'active':
        newFilters.status = 'active';
        break;
      case 'blocked':
        newFilters.status = 'blocked';
        break;
      case 'sub':
        newFilters.subscription = 'true';
        break;
      case 'complaints':
        newFilters.hasComplaints = 'true';
        break;
      case 'consultations':
        newFilters.hasConsultations = 'true';
        break;
      case 'vip':
        newFilters.spentMin = '30000';
        break;
      case 'pendingOrders':
        newFilters.hasActiveOrders = 'true';
        break;
      case 'refunds':
        newFilters.status = 'all';
        break;
    }
    setFilters(newFilters);
    setCurrentPage(1);
  };
  // --- RENDERERS ---
  const renderKpiCard = (label: string, value: string | number, icon: React.ReactNode, bgClass: string, textClass: string, filterKey?: string) => <Card padding="sm" hoverable className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer group" onClick={() => filterKey ? applyKpiFilter(filterKey) : applyKpiFilter('all')} data-id="element-7581">
      <div className="flex justify-between items-start" data-id="element-7582">
        <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-7583">
          {icon}
        </div>
      </div>
      <div data-id="element-7584">
        <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-7585">{label}</div>
        <div className="text-xl font-bold text-slate-900" data-id="element-7586">{value}</div>
      </div>
    </Card>;
  const renderUserDetailPanel = () => {
    if (!selectedUser) return null;
    const mockReports = [{
      id: 1,
      type: 'Натальная карта',
      date: selectedUser.lastOrder || '10 Окт 2024',
      status: 'ready'
    }, {
      id: 2,
      type: 'Совместимость',
      date: '05 Окт 2024',
      status: 'ready'
    }, {
      id: 3,
      type: 'Прогноз транзитов',
      date: '01 Окт 2024',
      status: 'processing'
    }];
    const mockConsultations = [{
      id: 1,
      type: 'Видео-консультация',
      astrologer: 'Мария Звёздная',
      duration: '45 мин',
      date: '08 Окт 2024',
      status: 'completed'
    }, {
      id: 2,
      type: 'Чат-консультация',
      astrologer: 'Павел Орион',
      duration: '30 мин',
      date: '25 Сен 2024',
      status: 'completed'
    }];
    const mockChats = [{
      id: 1,
      with: 'Мария Звёздная',
      role: 'Астролог',
      lastMsg: 'Спасибо за консультацию! Подскажите ещё...',
      time: '10 мин назад',
      unread: 1
    }, {
      id: 2,
      with: 'Поддержка',
      role: 'Модератор',
      lastMsg: 'Ваш вопрос решён. Обращайтесь!',
      time: 'Вчера',
      unread: 0
    }];
    const mockOrders = [...Array(Math.min(parseInt(selectedUser.orders) || 0, 5))].map((_, i) => ({
      id: 1000 + i,
      product: ['Натальная карта', 'Совместимость', 'Прогноз транзитов', 'Солнечный возврат', 'Комбо-анализ'][i % 5],
      price: [3900, 2900, 4500, 3200, 6900][i % 5],
      date: selectedUser.lastOrder || '10 Окт 2024',
      status: i === 0 ? 'processing' : 'completed'
    }));
    const tabs = [{
      id: 'basic',
      label: 'Основное',
      content: <div className="space-y-6" data-id="element-7587">
            <div className="flex items-start gap-4" data-id="element-7588">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-2xl" data-id="element-7589">
                {selectedUser.name.split(' ').map((n: string) => n[0]).join('')}
              </div>
              <div className="flex-1" data-id="element-7590">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-7591">
                  {selectedUser.name}
                  {selectedUser.isVip && <Star className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-7592" />}
                </h2>
                <p className="text-sm text-slate-500 mb-2" data-id="element-7593">
                  {selectedUser.email} • ID: {selectedUser.id}
                </p>
                <div className="flex gap-2" data-id="element-7594">
                  <Badge variant={selectedUser.status === 'active' ? 'success' : 'error'} data-id="element-7595">
                    {selectedUser.status === 'active' ? 'Активен' : 'Заблокирован'}
                  </Badge>
                  {selectedUser.subscription && <Badge variant="pro" data-id="element-7596">{selectedUser.subscription}</Badge>}
                </div>
              </div>
            </div>

            {/* Quick Navigation */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-7597">
              <button onClick={() => {
            setSelectedUser(null);
            setActiveTab('orders');
          }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors group" data-id="element-7598">
                <ShoppingBag className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 transition-colors" data-id="element-7599" />
                <span className="text-[11px] font-medium text-slate-600 group-hover:text-indigo-700" data-id="element-7600">
                  Заказы ({selectedUser.orders})
                </span>
              </button>
              <button onClick={() => {
            setSelectedUser(null);
            setActiveTab('full_chat');
          }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors group" data-id="element-7601">
                <MessageSquare className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" data-id="element-7602" />
                <span className="text-[11px] font-medium text-slate-600 group-hover:text-blue-700" data-id="element-7603">
                  Чаты
                </span>
              </button>
              <button onClick={() => {
            setSelectedUser(null);
            setActiveTab('ai_reports');
          }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-colors group" data-id="element-7604">
                <FileText className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors" data-id="element-7605" />
                <span className="text-[11px] font-medium text-slate-600 group-hover:text-purple-700" data-id="element-7606">
                  Отчёты
                </span>
              </button>
              <button onClick={() => {
            setSelectedUser(null);
            setActiveTab('consultations');
          }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-colors group" data-id="element-7607">
                <Video className="w-5 h-5 text-slate-400 group-hover:text-teal-600 transition-colors" data-id="element-7608" />
                <span className="text-[11px] font-medium text-slate-600 group-hover:text-teal-700" data-id="element-7609">
                  Консультации ({selectedUser.consultations})
                </span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-7610">
              <Card padding="sm" className="bg-slate-50 border-none shadow-none" data-id="element-7611">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-7612">
                  <Phone className="w-3 h-3" data-id="element-7613" /> Телефон
                </div>
                <div className="text-sm font-medium" data-id="element-7614">{selectedUser.phone}</div>
              </Card>
              <Card padding="sm" className="bg-slate-50 border-none shadow-none" data-id="element-7615">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-7616">
                  <MapPin className="w-3 h-3" data-id="element-7617" /> Страна
                </div>
                <div className="text-sm font-medium" data-id="element-7618">
                  {selectedUser.country}
                </div>
              </Card>
              <Card padding="sm" className="bg-slate-50 border-none shadow-none" data-id="element-7619">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-7620">
                  <Calendar className="w-3 h-3" data-id="element-7621" /> Регистрация
                </div>
                <div className="text-sm font-medium" data-id="element-7622">{selectedUser.date}</div>
              </Card>
              <Card padding="sm" className="bg-slate-50 border-none shadow-none" data-id="element-7623">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-7624">
                  <Clock className="w-3 h-3" data-id="element-7625" /> Последний вход
                </div>
                <div className="text-sm font-medium" data-id="element-7626">
                  {selectedUser.lastLogin}
                </div>
              </Card>
            </div>

            {/* Balance Management */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4" data-id="element-7627">
              <div className="flex items-center justify-between mb-3" data-id="element-7628">
                <div data-id="element-7629">
                  <div className="text-xs text-indigo-600 font-medium mb-0.5" data-id="element-7630">
                    Баланс пользователя
                  </div>
                  <div className="text-2xl font-bold text-slate-900" data-id="element-7631">
                    {(selectedUser.balance || 0).toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center" data-id="element-7632">
                  <CreditCard className="w-5 h-5 text-indigo-600" data-id="element-7633" />
                </div>
              </div>
              <div className="flex gap-2" data-id="element-7634">
                {[1000, 3000, 5000].map(amt => <button key={amt} onClick={() => {
              setUsers(users.map((u: any) => u.id === selectedUser.id ? {
                ...u,
                balance: (u.balance || 0) + amt
              } : u));
              setSelectedUser({
                ...selectedUser,
                balance: (selectedUser.balance || 0) + amt
              });
              success(`Баланс пользователя ${selectedUser.name} пополнен на ${amt.toLocaleString('ru-RU')} ₽`);
            }} className="flex-1 py-1.5 text-xs font-medium bg-white border border-indigo-200 rounded-lg text-indigo-700 hover:bg-indigo-50 transition-colors" data-id="element-7635">
                    +{amt.toLocaleString('ru-RU')} ₽
                  </button>)}
              </div>
              <button onClick={() => success(`Открыта история операций ${selectedUser.name}`)} className="w-full mt-2 text-center text-[11px] text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-7636">
                История операций →
              </button>
            </div>

            <div className="space-y-4" data-id="element-7637">
              <div className="flex justify-between items-center" data-id="element-7638">
                <h3 className="font-bold text-slate-900" data-id="element-7639">Внутренние заметки</h3>
                <Button variant="ghost" size="sm" icon={<Edit3 className="w-4 h-4" data-id="element-7641" />} onClick={() => setNoteModal({
              userId: selectedUser.id,
              text: selectedUser.notes
            })} data-id="element-7640">
                  Редактировать
                </Button>
              </div>
              {selectedUser.notes ? <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 whitespace-pre-wrap" data-id="element-7642">
                  {selectedUser.notes}
                </div> : <div className="text-sm text-slate-400 italic" data-id="element-7643">Нет заметок</div>}
              <div className="flex justify-between items-center mt-6" data-id="element-7644">
                <h3 className="font-bold text-slate-900" data-id="element-7645">Risk Flag</h3>
                <Button variant="ghost" size="sm" icon={<Edit3 className="w-4 h-4" data-id="element-7647" />} onClick={() => setRiskModal({
              userId: selectedUser.id,
              flag: selectedUser.riskFlag || ''
            })} data-id="element-7646">
                  Редактировать
                </Button>
              </div>
              {selectedUser.riskFlag ? <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2" data-id="element-7648">
                  <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-7649" />
                  <span className="text-sm text-rose-800 font-medium" data-id="element-7650">
                    {selectedUser.riskFlag}
                  </span>
                </div> : <div className="text-sm text-slate-400 italic" data-id="element-7651">
                  Нет флагов риска
                </div>}
            </div>
          </div>
    }, {
      id: 'orders',
      label: `Заказы (${selectedUser.orders})`,
      content: <div className="space-y-4" data-id="element-7652">
            <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg" data-id="element-7653">
              <span className="text-sm text-slate-600" data-id="element-7654">Всего потрачено:</span>
              <span className="font-bold text-lg text-slate-900" data-id="element-7655">
                {selectedUser.spent}
              </span>
            </div>
            {mockOrders.map(order => <Card key={order.id} padding="sm" className="border-slate-200 shadow-none cursor-pointer hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('orders');
        }} data-id="element-7656">
                <div className="flex justify-between items-center" data-id="element-7657">
                  <div data-id="element-7658">
                    <div className="font-medium text-sm text-slate-900 flex items-center gap-2" data-id="element-7659">
                      Заказ #ORD-{order.id}
                      <span className="text-indigo-500 text-[10px]" data-id="element-7660">
                        → открыть
                      </span>
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-7661">
                      {order.product} • {order.date}
                    </div>
                  </div>
                  <div className="text-right" data-id="element-7662">
                    <div className="font-bold text-sm" data-id="element-7663">
                      {order.price.toLocaleString()} ₽
                    </div>
                    <Badge variant={order.status === 'completed' ? 'success' : 'warning'} size="sm" data-id="element-7664">
                      {order.status === 'completed' ? 'Выполнен' : 'В работе'}
                    </Badge>
                  </div>
                </div>
              </Card>)}
            {mockOrders.length === 0 && <div className="text-center py-8 text-slate-500" data-id="element-7665">Нет заказов</div>}
            {parseInt(selectedUser.orders) > 0 && <button onClick={() => {
          setSelectedUser(null);
          setActiveTab('orders');
        }} className="w-full text-center py-2.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-7666">
                Все заказы пользователя →
              </button>}
          </div>
    }, {
      id: 'reports',
      label: 'Отчёты',
      content: <div className="space-y-4" data-id="element-7667">
            {mockReports.slice(0, parseInt(selectedUser.orders) || 0).map(report => <Card key={report.id} padding="sm" className="border-slate-200 shadow-none cursor-pointer hover:border-purple-200 hover:bg-purple-50/30 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('ai_reports');
        }} data-id="element-7668">
                  <div className="flex justify-between items-center" data-id="element-7669">
                    <div className="flex items-center gap-3" data-id="element-7670">
                      <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center" data-id="element-7671">
                        <FileText className="w-5 h-5 text-purple-600" data-id="element-7672" />
                      </div>
                      <div data-id="element-7673">
                        <div className="font-medium text-sm text-slate-900 flex items-center gap-2" data-id="element-7674">
                          {report.type}
                          <span className="text-purple-500 text-[10px]" data-id="element-7675">
                            → открыть
                          </span>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-7676">
                          {report.date}
                        </div>
                      </div>
                    </div>
                    <Badge variant={report.status === 'ready' ? 'success' : 'warning'} size="sm" data-id="element-7677">
                      {report.status === 'ready' ? 'Готов' : 'Генерация...'}
                    </Badge>
                  </div>
                </Card>)}
            {(parseInt(selectedUser.orders) || 0) === 0 && <div className="text-center py-8 text-slate-500" data-id="element-7678">Нет отчётов</div>}
            <button onClick={() => {
          setSelectedUser(null);
          setActiveTab('ai_reports');
        }} className="w-full text-center py-2.5 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors" data-id="element-7679">
              Все отчёты в системе →
            </button>
          </div>
    }, {
      id: 'consultations',
      label: `Консультации (${selectedUser.consultations})`,
      content: <div className="space-y-4" data-id="element-7680">
            {mockConsultations.slice(0, selectedUser.consultations).map(cons => <Card key={cons.id} padding="sm" className="border-slate-200 shadow-none cursor-pointer hover:border-teal-200 hover:bg-teal-50/30 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('consultations');
        }} data-id="element-7681">
                  <div className="flex justify-between items-center" data-id="element-7682">
                    <div className="flex items-center gap-3" data-id="element-7683">
                      <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center" data-id="element-7684">
                        <Video className="w-5 h-5 text-teal-600" data-id="element-7685" />
                      </div>
                      <div data-id="element-7686">
                        <div className="font-medium text-sm text-slate-900 flex items-center gap-2" data-id="element-7687">
                          {cons.type}
                          <span className="text-teal-500 text-[10px]" data-id="element-7688">
                            → открыть
                          </span>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-7689">
                          Астролог: {cons.astrologer} • {cons.duration} •{' '}
                          {cons.date}
                        </div>
                      </div>
                    </div>
                    <Badge variant="neutral" size="sm" data-id="element-7690">
                      Завершена
                    </Badge>
                  </div>
                </Card>)}
            {selectedUser.consultations === 0 && <div className="text-center py-8 text-slate-500" data-id="element-7691">
                Нет консультаций
              </div>}
            {selectedUser.consultations > 0 && <button onClick={() => {
          setSelectedUser(null);
          setActiveTab('consultations');
        }} className="w-full text-center py-2.5 text-sm font-medium text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-7692">
                Все консультации →
              </button>}
          </div>
    }, {
      id: 'chats',
      label: 'Чаты',
      content: <div className="space-y-4" data-id="element-7693">
            {selectedUser.hasActiveChats ? mockChats.map(chat => <Card key={chat.id} padding="sm" className="border-slate-200 shadow-none cursor-pointer hover:border-blue-200 hover:bg-blue-50/30 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('full_chat');
        }} data-id="element-7694">
                  <div className="flex justify-between items-center" data-id="element-7695">
                    <div className="flex items-center gap-3" data-id="element-7696">
                      <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center" data-id="element-7697">
                        <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-7698" />
                      </div>
                      <div data-id="element-7699">
                        <div className="font-medium text-sm text-slate-900 flex items-center gap-2" data-id="element-7700">
                          {chat.with}
                          <Badge variant="neutral" size="sm" data-id="element-7701">
                            {chat.role}
                          </Badge>
                          <span className="text-blue-500 text-[10px]" data-id="element-7702">
                            → открыть
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 truncate max-w-[250px]" data-id="element-7703">
                          {chat.lastMsg}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-1" data-id="element-7704">
                      <div className="text-[10px] text-slate-400" data-id="element-7705">
                        {chat.time}
                      </div>
                      {chat.unread > 0 && <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-id="element-7706">
                          {chat.unread}
                        </span>}
                    </div>
                  </div>
                </Card>) : <div className="text-center py-8 text-slate-500" data-id="element-7707">
                Нет активных диалогов
              </div>}
            <button onClick={() => {
          setSelectedUser(null);
          setActiveTab('full_chat');
        }} className="w-full text-center py-2.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-7708">
              Открыть чат-центр →
            </button>
          </div>
    }, {
      id: 'complaints',
      label: 'Жалобы',
      content: <div className="space-y-4" data-id="element-7709">
            {selectedUser.hasComplaints ? <Card padding="sm" className="border-rose-100 bg-rose-50 shadow-none cursor-pointer hover:bg-rose-100/50 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('moderation');
        }} data-id="element-7710">
                <div className="flex justify-between items-start mb-2" data-id="element-7711">
                  <Badge variant="error" size="sm" data-id="element-7712">
                    Открыта
                  </Badge>
                  <span className="text-xs text-slate-500" data-id="element-7713">Вчера</span>
                </div>
                <div className="font-medium text-sm text-slate-900 mb-1 flex items-center gap-2" data-id="element-7714">
                  Жалоба на астролога (Павел Орион)
                  <span className="text-rose-500 text-[10px]" data-id="element-7715">→ модерация</span>
                </div>
                <p className="text-sm text-slate-700" data-id="element-7716">
                  Астролог опоздал на консультацию на 15 минут и общался грубо.
                  Прошу вернуть деньги.
                </p>
              </Card> : <div className="text-center py-8 text-slate-500" data-id="element-7717">Нет жалоб</div>}
          </div>
    }, {
      id: 'activity',
      label: 'Активность',
      content: <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-7718">
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-7719">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-indigo-100 text-indigo-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-7720">
                <Clock className="w-4 h-4" data-id="element-7721" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-7722">
                <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-7723">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-7724">
                    Вход в систему
                  </div>
                  <time className="text-xs font-medium text-slate-500" data-id="element-7725">
                    {selectedUser.lastLogin}
                  </time>
                </div>
                <div className="text-sm text-slate-500" data-id="element-7726">
                  IP: 192.168.1.1 • {selectedUser.country}
                </div>
              </div>
            </div>
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-7727">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-emerald-100 text-emerald-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-7728">
                <ShoppingBag className="w-4 h-4" data-id="element-7729" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm cursor-pointer hover:border-emerald-200 transition-colors" onClick={() => {
            setSelectedUser(null);
            setActiveTab('orders');
          }} data-id="element-7730">
                <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-7731">
                  <div className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-7732">
                    Оформлен заказ{' '}
                    <span className="text-emerald-500 text-[10px]" data-id="element-7733">→</span>
                  </div>
                  <time className="text-xs font-medium text-slate-500" data-id="element-7734">
                    {selectedUser.lastOrder}
                  </time>
                </div>
                <div className="text-sm text-slate-500" data-id="element-7735">
                  Натальная карта (3 900 ₽)
                </div>
              </div>
            </div>
            {selectedUser.consultations > 0 && <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-7736">
                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-teal-100 text-teal-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-7737">
                  <Video className="w-4 h-4" data-id="element-7738" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm cursor-pointer hover:border-teal-200 transition-colors" onClick={() => {
            setSelectedUser(null);
            setActiveTab('consultations');
          }} data-id="element-7739">
                  <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-7740">
                    <div className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-7741">
                      Консультация{' '}
                      <span className="text-teal-500 text-[10px]" data-id="element-7742">→</span>
                    </div>
                    <time className="text-xs font-medium text-slate-500" data-id="element-7743">
                      05 Окт 2024
                    </time>
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-7744">
                    Видео с Мария Звёздная (45 мин)
                  </div>
                </div>
              </div>}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-7745">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-purple-100 text-purple-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-7746">
                <FileText className="w-4 h-4" data-id="element-7747" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm cursor-pointer hover:border-purple-200 transition-colors" onClick={() => {
            setSelectedUser(null);
            setActiveTab('ai_reports');
          }} data-id="element-7748">
                <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-7749">
                  <div className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-7750">
                    Отчёт сгенерирован{' '}
                    <span className="text-purple-500 text-[10px]" data-id="element-7751">→</span>
                  </div>
                  <time className="text-xs font-medium text-slate-500" data-id="element-7752">
                    {selectedUser.lastOrder}
                  </time>
                </div>
                <div className="text-sm text-slate-500" data-id="element-7753">
                  Натальная карта — AI отчёт
                </div>
              </div>
            </div>
          </div>
    }];
    return <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/20 backdrop-blur-sm" onClick={e => {
      if (e.target === e.currentTarget) setSelectedUser(null);
    }} data-id="element-7754">
        <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full animate-in slide-in-from-right" data-id="element-7755">
          <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/80 backdrop-blur-md" data-id="element-7756">
            <h3 className="text-base font-bold text-slate-900" data-id="element-7757">
              Карточка пользователя
            </h3>
            <div className="flex items-center gap-2" data-id="element-7758">
              <Button variant="outline" size="sm" icon={<Ban className="w-4 h-4" data-id="element-7760" />} onClick={() => handleToggleBlock(selectedUser.id)} data-id="element-7759">
                {selectedUser.status === 'active' ? 'Заблокировать' : 'Разблокировать'}
              </Button>
              <button className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => setSelectedUser(null)} data-id="element-7761">
                <X className="w-5 h-5" data-id="element-7762" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-7763">
            <div className="p-6" data-id="element-7764">
              <Tabs tabs={tabs} data-id="element-7765" />
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-7766">
      <div className="flex justify-between items-center" data-id="element-7767">
        <div data-id="element-7768">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-7769">Пользователи</h2>
          <p className="text-sm text-slate-500" data-id="element-7770">
            Управление клиентами платформы
          </p>
        </div>
        {setCreatePanel && <Button variant="primary" icon={<UserCheck className="w-4 h-4" data-id="element-7772" />} onClick={() => setCreatePanel({
        type: 'user',
        title: 'Добавить пользователя'
      })} data-id="element-7771">
            Добавить
          </Button>}
      </div>

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-id="element-7773">
        {renderKpiCard('Всего', kpis.total, <Users className="w-4 h-4 text-indigo-600" data-id="element-7774" />, 'bg-indigo-50', 'text-indigo-600')}
        {renderKpiCard('Новые (сегодня)', kpis.newToday, <Users className="w-4 h-4 text-emerald-600" data-id="element-7775" />, 'bg-emerald-50', 'text-emerald-600')}
        {renderKpiCard('Активные', kpis.active, <Activity className="w-4 h-4 text-blue-600" data-id="element-7776" />, 'bg-blue-50', 'text-blue-600', 'active')}
        {renderKpiCard('Заблокированные', kpis.blocked, <Ban className="w-4 h-4 text-rose-600" data-id="element-7777" />, 'bg-rose-50', 'text-rose-600', 'blocked')}
        {renderKpiCard('VIP клиенты', kpis.vip, <Star className="w-4 h-4 text-amber-600" data-id="element-7778" />, 'bg-amber-50', 'text-amber-600', 'vip')}
      </div>

      {/* KPI Cards - Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-id="element-7779">
        {renderKpiCard('С подпиской', kpis.withSub, <CreditCard className="w-4 h-4 text-purple-600" data-id="element-7780" />, 'bg-purple-50', 'text-purple-600', 'sub')}
        {renderKpiCard('С жалобами', kpis.withComplaints, <AlertTriangle className="w-4 h-4 text-orange-600" data-id="element-7781" />, 'bg-orange-50', 'text-orange-600', 'complaints')}
        {renderKpiCard('Ждут заказ', kpis.pendingOrders, <Clock className="w-4 h-4 text-sky-600" data-id="element-7782" />, 'bg-sky-50', 'text-sky-600', 'pendingOrders')}
        {renderKpiCard('Активные конс.', kpis.activeConsultations, <Video className="w-4 h-4 text-teal-600" data-id="element-7783" />, 'bg-teal-50', 'text-teal-600', 'consultations')}
        {renderKpiCard('Возвраты', kpis.refunds, <RefreshCw className="w-4 h-4 text-red-600" data-id="element-7784" />, 'bg-red-50', 'text-red-600', 'refunds')}
      </div>

      {/* Filter Bar */}
      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-7785">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-7786">
          <div className="flex items-center gap-3 w-full sm:w-auto flex-1" data-id="element-7787">
            <div className="relative flex-1 max-w-md" data-id="element-7788">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-7789" />
              <input type="text" placeholder="Поиск по имени, email, телефону, ID..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7790" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Search className="w-4 h-4" data-id="element-7792" />} onClick={() => setShowFilters(!showFilters)} data-id="element-7791">
              Фильтры
            </Button>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-7793">
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-7795" />} onClick={handleExport} data-id="element-7794">
              Экспорт CSV
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in slide-in-from-top-2" data-id="element-7796">
            {/* Row 1: Selects */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-7797">
              <div data-id="element-7798">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7799">
                  Статус
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-7800">
                  <option value="all" data-id="element-7801">Все</option>
                  <option value="active" data-id="element-7802">Активные</option>
                  <option value="blocked" data-id="element-7803">Заблокированные</option>
                </select>
              </div>
              <div data-id="element-7804">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7805">
                  Подписка
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.subscription} onChange={e => setFilters({
              ...filters,
              subscription: e.target.value
            })} data-id="element-7806">
                  <option value="all" data-id="element-7807">Все</option>
                  <option value="true" data-id="element-7808">Есть подписка</option>
                  <option value="false" data-id="element-7809">Нет подписки</option>
                </select>
              </div>
              <div data-id="element-7810">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7811">
                  Страна
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.country} onChange={e => setFilters({
              ...filters,
              country: e.target.value
            })} data-id="element-7812">
                  <option value="all" data-id="element-7813">Все</option>
                  <option value="Россия" data-id="element-7814">Россия</option>
                  <option value="Беларусь" data-id="element-7815">Беларусь</option>
                  <option value="Казахстан" data-id="element-7816">Казахстан</option>
                  <option value="Украина" data-id="element-7817">Украина</option>
                </select>
              </div>
              <div data-id="element-7818">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7819">
                  Жалобы
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.hasComplaints} onChange={e => setFilters({
              ...filters,
              hasComplaints: e.target.value
            })} data-id="element-7820">
                  <option value="all" data-id="element-7821">Все</option>
                  <option value="true" data-id="element-7822">Есть жалобы</option>
                  <option value="false" data-id="element-7823">Нет жалоб</option>
                </select>
              </div>
            </div>

            {/* Row 2: Consultations + Number ranges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-7824">
              <div data-id="element-7825">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7826">
                  Консультации
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.hasConsultations} onChange={e => setFilters({
              ...filters,
              hasConsultations: e.target.value
            })} data-id="element-7827">
                  <option value="all" data-id="element-7828">Все</option>
                  <option value="true" data-id="element-7829">Были консультации</option>
                  <option value="false" data-id="element-7830">Не было</option>
                </select>
              </div>
              <div data-id="element-7831">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7832">
                  Заказов
                </label>
                <div className="flex gap-2" data-id="element-7833">
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="от" value={filters.ordersMin} onChange={e => setFilters({
                ...filters,
                ordersMin: e.target.value
              })} data-id="element-7834" />
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="до" value={filters.ordersMax} onChange={e => setFilters({
                ...filters,
                ordersMax: e.target.value
              })} data-id="element-7835" />
                </div>
              </div>
              <div data-id="element-7836">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7837">
                  Потрачено (₽)
                </label>
                <div className="flex gap-2" data-id="element-7838">
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="от" value={filters.spentMin} onChange={e => setFilters({
                ...filters,
                spentMin: e.target.value
              })} data-id="element-7839" />
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="до" value={filters.spentMax} onChange={e => setFilters({
                ...filters,
                spentMax: e.target.value
              })} data-id="element-7840" />
                </div>
              </div>
              <div className="flex items-end" data-id="element-7841">
                <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              subscription: 'all',
              ordersMin: '',
              ordersMax: '',
              spentMin: '',
              spentMax: '',
              dateFrom: '',
              dateTo: '',
              country: 'all',
              language: 'all',
              hasComplaints: 'all',
              hasActiveOrders: 'all',
              hasConsultations: 'all'
            })} data-id="element-7842">
                  Сбросить
                </Button>
              </div>
            </div>
          </div>}
      </Card>
      {/* Mass Actions Bar */}
      {selectedUsers.size > 0 && <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-center justify-between animate-in fade-in" data-id="element-7843">
          <div className="flex items-center gap-3" data-id="element-7844">
            <Badge variant="pro" data-id="element-7845">{selectedUsers.size} выбрано</Badge>
            <span className="text-sm text-indigo-800 font-medium" data-id="element-7846">
              Массовые действия:
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-7847">
            <Button variant="outline" size="sm" className="bg-white" icon={<Mail className="w-4 h-4" data-id="element-7849" />} onClick={() => handleMassAction('email')} data-id="element-7848">
              Email
            </Button>
            <Button variant="outline" size="sm" className="bg-white" icon={<Tag className="w-4 h-4" data-id="element-7851" />} onClick={() => handleMassAction('tag')} data-id="element-7850">
              Тег
            </Button>
            <Button variant="outline" size="sm" className="bg-white" icon={<Download className="w-4 h-4" data-id="element-7853" />} onClick={() => handleMassAction('export')} data-id="element-7852">
              Экспорт
            </Button>
            <Button variant="outline" size="sm" className="bg-white text-rose-600 border-rose-200 hover:bg-rose-50" icon={<Ban className="w-4 h-4" data-id="element-7855" />} onClick={() => handleMassAction('block')} data-id="element-7854">
              Заблокировать
            </Button>
          </div>
        </div>}
      {/* Data Table */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-hidden" data-id="element-7856">
        <div className="overflow-x-auto" data-id="element-7857">
          <table className="w-full text-left" data-id="element-7858">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-7859">
              <tr data-id="element-7860">
                <th className="px-4 py-3 w-10" data-id="element-7861">
                  <button onClick={toggleSelectAll} className="text-slate-400 hover:text-indigo-600" data-id="element-7862">
                    {selectedUsers.size === currentUsers.length && currentUsers.length > 0 ? <CheckSquare className="w-4 h-4" data-id="element-7863" /> : <Square className="w-4 h-4" data-id="element-7864" />}
                  </button>
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-7865">Пользователь</th>
                <th className="px-4 py-3 font-medium" data-id="element-7866">Регистрация / Вход</th>
                <th className="px-4 py-3 font-medium" data-id="element-7867">Страна</th>
                <th className="px-4 py-3 font-medium" data-id="element-7868">Активность</th>
                <th className="px-4 py-3 font-medium" data-id="element-7869">Потрачено</th>
                <th className="px-4 py-3 font-medium" data-id="element-7870">Статус</th>
                <th className="px-4 py-3 text-right font-medium" data-id="element-7871">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-7872">
              {currentUsers.map(user => <tr key={user.id} className={`hover:bg-slate-50 transition-colors group ${selectedUsers.has(user.id) ? 'bg-indigo-50/30' : ''}`} data-id="element-7873">
                  <td className="px-4 py-3" data-id="element-7874">
                    <button onClick={() => toggleSelectUser(user.id)} className={`hover:text-indigo-600 ${selectedUsers.has(user.id) ? 'text-indigo-600' : 'text-slate-300'}`} data-id="element-7875">
                      {selectedUsers.has(user.id) ? <CheckSquare className="w-4 h-4" data-id="element-7876" /> : <Square className="w-4 h-4" data-id="element-7877" />}
                    </button>
                  </td>
                  <td className="px-4 py-3" data-id="element-7878">
                    <div className="flex items-center gap-3" data-id="element-7879">
                      <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-xs shrink-0" data-id="element-7880">
                        {user.name.split(' ').map((n: string) => n[0]).join('')}
                      </div>
                      <div className="min-w-0" data-id="element-7881">
                        <div className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 cursor-pointer truncate flex items-center gap-1" onClick={() => setSelectedUser(user)} data-id="element-7882">
                          {user.name}
                          {user.isVip && <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" title="VIP клиент" data-id="element-7883" />}
                        </div>
                        <div className="text-xs text-slate-500 truncate" data-id="element-7884">
                          {user.email}
                        </div>

                        {/* Indicators Row */}
                        <div className="flex items-center gap-1.5 mt-1" data-id="element-7885">
                          {user.hasActiveChats && <span className="relative group/tip cursor-pointer" data-id="element-7886">
                              <MessageSquare className="w-3.5 h-3.5 text-blue-500" data-id="element-7887" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-slate-800 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-7888">
                                💬 Есть активные чаты
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-7889" />
                              </span>
                            </span>}
                          {user.hasComplaints && <span className="relative group/tip cursor-pointer" data-id="element-7890">
                              <AlertTriangle className="w-3.5 h-3.5 text-orange-500" data-id="element-7891" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-slate-800 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-7892">
                                ⚠️ Есть жалобы на пользователя
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-7893" />
                              </span>
                            </span>}
                          {user.hasPaymentIssues && <span className="relative group/tip cursor-pointer" data-id="element-7894">
                              <CreditCard className="w-3.5 h-3.5 text-rose-500" data-id="element-7895" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-slate-800 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-7896">
                                💳 Проблемы с оплатой
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-7897" />
                              </span>
                            </span>}
                          {user.riskFlag && <span className="relative group/tip cursor-pointer" data-id="element-7898">
                              <AlertTriangle className="w-3.5 h-3.5 text-rose-600" data-id="element-7899" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-rose-700 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-7900">
                                🚨 Risk: {user.riskFlag}
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-rose-700" data-id="element-7901" />
                              </span>
                            </span>}
                          {user.notes && <span className="relative group/tip cursor-pointer" data-id="element-7902">
                              <FileText className="w-3.5 h-3.5 text-amber-500" data-id="element-7903" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-slate-800 text-white text-[10px] font-medium rounded-lg max-w-[200px] truncate opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-7904">
                                📝 {user.notes || 'Есть заметка'}
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-7905" />
                              </span>
                            </span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-7906">
                    <div className="text-xs text-slate-900" data-id="element-7907">{user.date}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-7908">
                      Вход: {user.lastLogin}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-id="element-7909">
                    {user.country}
                  </td>
                  <td className="px-4 py-3" data-id="element-7910">
                    <div className="flex gap-3" data-id="element-7911">
                      <div className="text-center" title="Заказы" data-id="element-7912">
                        <div className="text-xs font-bold text-slate-900" data-id="element-7913">
                          {user.orders}
                        </div>
                        <div className="text-[9px] text-slate-500 uppercase" data-id="element-7914">
                          Зак
                        </div>
                      </div>
                      <div className="text-center" title="Консультации" data-id="element-7915">
                        <div className="text-xs font-bold text-slate-900" data-id="element-7916">
                          {user.consultations}
                        </div>
                        <div className="text-[9px] text-slate-500 uppercase" data-id="element-7917">
                          Конс
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-7918">
                    <div className="text-sm font-medium text-slate-900" data-id="element-7919">
                      {user.spent}
                    </div>
                    {user.subscription && <Badge variant={user.subscription === 'Pro' ? 'pro' : 'featured'} size="sm" className="mt-1" data-id="element-7920">
                        {user.subscription}
                      </Badge>}
                  </td>
                  <td className="px-4 py-3" data-id="element-7921">
                    <Badge variant={user.status === 'active' ? 'success' : 'error'} size="sm" data-id="element-7922">
                      {user.status === 'active' ? 'Активен' : 'Заблокирован'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right relative" data-id="element-7923">
                    <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100" icon={<MoreVertical className="w-4 h-4" data-id="element-7925" />} onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)} data-id="element-7924" />

                    {/* Action Dropdown */}
                    {actionMenuOpen === user.id && <>
                        <div className="fixed inset-0 z-10" onClick={() => setActionMenuOpen(null)} data-id="element-7926" />
                        <div className="absolute right-8 top-10 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-20 text-left animate-in fade-in zoom-in-95" data-id="element-7927">
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setSelectedUser(user);
                      setActionMenuOpen(null);
                    }} data-id="element-7928">
                            <Eye className="w-3.5 h-3.5" data-id="element-7929" /> Открыть профиль
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setActiveTab('orders');
                      setActionMenuOpen(null);
                    }} data-id="element-7930">
                            <ShoppingBag className="w-3.5 h-3.5" data-id="element-7931" /> Открыть
                            заказы
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setActiveTab('full_chat');
                      setActionMenuOpen(null);
                    }} data-id="element-7932">
                            <MessageSquare className="w-3.5 h-3.5" data-id="element-7933" /> Открыть
                            чаты
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setActiveTab('ai_reports');
                      setActionMenuOpen(null);
                    }} data-id="element-7934">
                            <Zap className="w-3.5 h-3.5" data-id="element-7935" /> Открыть отчёты
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setActiveTab('consultations');
                      setActionMenuOpen(null);
                    }} data-id="element-7936">
                            <Video className="w-3.5 h-3.5" data-id="element-7937" /> Консультации
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-7938"></div>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setNoteModal({
                        userId: user.id,
                        text: user.notes
                      });
                      setActionMenuOpen(null);
                    }} data-id="element-7939">
                            <Edit3 className="w-3.5 h-3.5" data-id="element-7940" /> Добавить заметку
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setRiskModal({
                        userId: user.id,
                        flag: user.riskFlag || ''
                      });
                      setActionMenuOpen(null);
                    }} data-id="element-7941">
                            <AlertTriangle className="w-3.5 h-3.5" data-id="element-7942" /> Risk-флаг
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-7943"></div>
                          <button className="w-full px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => handleToggleBlock(user.id)} data-id="element-7944">
                            {user.status === 'active' ? <Ban className="w-3.5 h-3.5" data-id="element-7945" /> : <UserCheck className="w-3.5 h-3.5" data-id="element-7946" />}
                            {user.status === 'active' ? 'Заблокировать' : 'Разблокировать'}
                          </button>
                        </div>
                      </>}
                  </td>
                </tr>)}
              {currentUsers.length === 0 && <tr data-id="element-7947">
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-500" data-id="element-7948">
                    Пользователи не найдены по заданным фильтрам
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-7949">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-7950" />
          </div>}
      </Card>

      {/* Detail Panel */}
      {renderUserDetailPanel()}
      {/* Note Modal */}
      {noteModal && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm" onClick={e => {
      if (e.target === e.currentTarget) setNoteModal(null);
    }} data-id="element-7951">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6" data-id="element-7952">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-7953">
              Внутренняя заметка
            </h3>
            <p className="text-xs text-slate-500 mb-4" data-id="element-7954">
              Эта заметка видна только администраторам.
            </p>
            <textarea className="w-full border border-slate-200 rounded-lg p-3 text-sm min-h-[120px] focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 mb-4" placeholder="Например: VIP клиент, часто жалуется..." value={noteModal.text} onChange={e => setNoteModal({
          ...noteModal,
          text: e.target.value
        })} data-id="element-7955" />
            <div className="flex justify-end gap-3" data-id="element-7956">
              <Button variant="ghost" onClick={() => setNoteModal(null)} data-id="element-7957">
                Отмена
              </Button>
              <Button variant="primary" onClick={handleSaveNote} data-id="element-7958">
                Сохранить
              </Button>
            </div>
          </div>
        </div>}
      {/* Risk Flag Modal */}
      {riskModal && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm" onClick={e => {
      if (e.target === e.currentTarget) setRiskModal(null);
    }} data-id="element-7959">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6" data-id="element-7960">
            <div className="flex items-center gap-2 mb-4" data-id="element-7961">
              <AlertTriangle className="w-5 h-5 text-rose-600" data-id="element-7962" />
              <h3 className="text-lg font-bold text-slate-900" data-id="element-7963">Risk Flag</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4" data-id="element-7964">
              Установите флаг риска для привлечения внимания модераторов.
            </p>
            <Input label="Причина риска" placeholder="Например: подозрение на мошенничество" value={riskModal.flag} onChange={e => setRiskModal({
          ...riskModal,
          flag: e.target.value
        })} data-id="element-7965" />
            <div className="flex gap-2 mt-3 mb-6" data-id="element-7966">
              <button className="text-[10px] bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded text-slate-600" onClick={() => setRiskModal({
            ...riskModal,
            flag: 'Конфликтный'
          })} data-id="element-7967">
                Конфликтный
              </button>
              <button className="text-[10px] bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded text-slate-600" onClick={() => setRiskModal({
            ...riskModal,
            flag: 'Передача контактов'
          })} data-id="element-7968">
                Передача контактов
              </button>
              <button className="text-[10px] bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded text-slate-600" onClick={() => setRiskModal({
            ...riskModal,
            flag: 'Мошенничество'
          })} data-id="element-7969">
                Мошенничество
              </button>
            </div>
            <div className="flex justify-between items-center" data-id="element-7970">
              <Button variant="ghost" className="text-rose-600" onClick={() => {
            setRiskModal({
              ...riskModal,
              flag: ''
            });
            handleSaveRisk();
          }} data-id="element-7971">
                Снять флаг
              </Button>
              <div className="flex gap-3" data-id="element-7972">
                <Button variant="ghost" onClick={() => setRiskModal(null)} data-id="element-7973">
                  Отмена
                </Button>
                <Button variant="primary" onClick={handleSaveRisk} data-id="element-7974">
                  Сохранить
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}